import { slide } from 'book-of-spells/src/animations.mjs';
import { readOptions } from 'book-of-spells/src/dom.mjs';
import { ElementBase, define, nextIndex } from '../../core.js';

// The header key map is shared plumbing now that the menu walks its items the same
// way. Still exported from here: it was part of this module's surface first.
export { nextIndex };

const OPTIONS = { exclusive: 'boolean' };

/** Class of the box the element animates. The library's, kept inert. */
const WRAPPER_CLASS = 'accordion-elemental-content-wrapper';

/** Class of the box inside it that holds the panel body. The page's to style. */
const CONTENT_CLASS = 'accordion-elemental-content';

// A closing panel keeps `open` for the length of the slide, so `[open]` alone
// cannot tell a stylesheet which way the panel is heading. This class can, and it
// is the one piece of the closing state deliberately made stylable - a marker on
// the summary, say, needs to start turning back on the first frame of the close
// rather than the last.
const CLOSING_CLASS = 'accordion-elemental-closing';

// Kept off the attribute surface so it cannot be styled, serialised or collided
// with by the page.
const DETACHED_NAME = Symbol('detachedName');

// Monotonic counter for generating unique `name` values for exclusive groups.
let groupCount = 0;

/**
 * Which panels of an exclusive group are left open, given how they were authored: the
 * first one with `open` on it, and no other.
 *
 * The decision has to be made before the shared `name` goes on. Naming several
 * already-open panels leaves it to the browser which of them survives, and a group that
 * opens on a different panel depending on the engine is not a group anybody authored.
 * Choosing here makes it the first, everywhere.
 *
 * @param {boolean[]} states - `open` of each panel, in document order.
 * @returns {boolean[]} What each panel's `open` should be.
 */
export function exclusiveOpen(states) {
  let seen = false;
  return states.map((open) => {
    const keep = open && !seen;
    seen = seen || open;
    return keep;
  });
}

/**
 * `<accordion-elemental>` custom element.
 *
 * A thin coordinator over native `<details>`/`<summary>` rather than a
 * reimplementation of the APG Accordion pattern. Native already provides the
 * disclosure semantics, Enter/Space activation, screen-reader announcement and
 * find-in-page expansion. This element only adds what native leaves out:
 *
 * - `exclusive`: assigns a shared `name` so only one panel stays open
 * - arrow/Home/End navigation between headers (APG accordion, optional but recommended)
 * - opening the panel that contains the current URL fragment
 * - an `accordion-toggle` event on the group, since `toggle` does not bubble
 * - a height animation on open and close
 *
 * Light DOM, no shadow root, so every part stays stylable by the page author.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 *
 * @tag accordion-elemental
 * @attr {boolean} [exclusive=false] - Only one panel open at a time.
 * @attr {string} name - The shared `name` `exclusive` assigns. Generated if unset.
 *
 * @cssprop {<time>} [--accordion-elemental-duration=250ms] - How long a panel takes to slide. The element reads it back out of the computed styles, so `0s` toggles instantly.
 * @cssprop {<easing-function>} [--accordion-elemental-easing=ease] - How the slide moves.
 * @cssprop {<color>} [--accordion-elemental-border-color=color-mix(in srgb, currentcolor 15%, transparent)] - Rule between the panels.
 * @cssprop {<length>} [--accordion-elemental-radius=0.5rem] - Corner radius, on the grouped look.
 *
 * @fires accordion-toggle - `detail.panel` is the `<details>` that toggled, `detail.open` its new state. On the group, because `toggle` does not bubble.
 *
 * @slot - The `<details>` panels, each with its own `<summary>`.
 */
export class AccordionElemental extends ElementBase {
  /** Direct-child panels only, so a nested accordion is not swallowed. */
  get panels() {
    return Array.from(this.querySelectorAll(':scope > details'));
  }

  /** The `<summary>` of each panel, in document order. */
  get headers() {
    return this.panels
      .map((panel) => panel.querySelector(':scope > summary'))
      .filter(Boolean);
  }

  connectedCallback() {
    // Wait until the light-DOM panels have been parsed. The bundle is loaded
    // deferred or at the end of the body, so by upgrade time they are there.
    if (this.initialized || !this.panels.length) return;
    this.initialized = true;

    this.options = Object.assign({ exclusive: false }, readOptions(this, OPTIONS));

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onHashChange = this.onHashChange.bind(this);

    this.wrapPanels();
    if (this.options.exclusive) this.applyExclusive();

    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('click', this.onClick);
    // `toggle` does not bubble, but non-bubbling events still run the capture
    // phase through ancestors - so capture is how the group hears its panels.
    this.addEventListener('toggle', this.onToggle, true);
    window.addEventListener('hashchange', this.onHashChange);

    this.openFromHash();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('toggle', this.onToggle, true);
    window.removeEventListener('hashchange', this.onHashChange);
    this.initialized = false;
  }

  /**
   * Wrap each panel body in two divs, because a height transition needs one box to
   * measure and clip and `<details>` hands you a bare run of siblings. Idempotent,
   * so moving the group in the DOM does not nest a second wrapper.
   *
   * Two rather than one because the box being animated cannot be padded: block
   * padding is a floor the height cannot get under, since `box-sizing: border-box`
   * renders `height: 0` as the padding, and the panel would slide shut down to it
   * and then cut. The outer box is the library's and stays inert; the inner one is
   * where a stylesheet puts the panel's inset, on one box rather than spread over
   * whichever children it thought to name.
   *
   * ponytail: `::details-content` is the wrapper the platform already has, but
   * animating it from 0 to `auto` also needs `interpolate-size`, which is not
   * everywhere yet. Drop the outer div for the pseudo-element once it is.
   */
  wrapPanels() {
    for (const panel of this.panels) {
      const summary = panel.querySelector(':scope > summary');
      if (!summary) continue;
      if (panel.querySelector(':scope > .' + WRAPPER_CLASS)) continue;

      const wrapper = document.createElement('div');
      wrapper.className = WRAPPER_CLASS;
      const content = document.createElement('div');
      content.className = CONTENT_CLASS;
      wrapper.appendChild(content);

      // Everything after the summary is the panel body.
      let node = summary.nextSibling;
      while (node) {
        const next = node.nextSibling;
        content.appendChild(node);
        node = next;
      }
      panel.appendChild(wrapper);
    }
  }

  /** @returns {HTMLElement|null} The box a panel's height is animated on. */
  wrapperOf(panel) {
    return panel.querySelector(':scope > .' + WRAPPER_CLASS);
  }

  /**
   * Give every panel the same `name`, which is what makes native `<details>`
   * mutually exclusive.
   */
  applyExclusive() {
    const panels = this.panels;

    // Close the extras *before* naming them - see `exclusiveOpen`.
    const open = exclusiveOpen(panels.map((panel) => panel.open));
    panels.forEach((panel, at) => { panel.open = open[at]; });

    // Remember the name so moving the group in the DOM re-uses it instead of
    // minting a new one on every reconnect. A name already on the panels is kept:
    // that is how an author gets exclusivity with scripting off, since without a
    // shared `name` in the markup there is nothing for the browser to enforce.
    if (!this.groupName) {
      this.groupName = this.getAttribute('name')
        || (panels[0] && panels[0].getAttribute('name'))
        || 'accordion-elemental-' + (++groupCount);
    }
    for (const panel of panels) panel.name = this.groupName;
  }

  /**
   * Open a panel and slide its body down. The panel opens first, since the body
   * is `display: none` until it does and an unrendered box has no height.
   */
  openPanel(panel) {
    const wrapper = this.wrapperOf(panel);
    if (!wrapper) {
      panel.open = true;
      return;
    }

    // Close the siblings here rather than leaving it to the shared `name`, which
    // would shut them instantly the moment this one opens.
    if (this.options.exclusive) {
      for (const other of this.panels) {
        if (other !== panel && other.open && !other.classList.contains(CLOSING_CLASS)) {
          this.closePanel(other);
        }
      }
    }

    // Measure before opening. Once `open` is set the body is already at full
    // height, and a slide that starts where it ends is not a slide. A panel
    // caught mid-close is rendered already, so its height is the honest start.
    const from = panel.open ? wrapper.offsetHeight : 0;

    panel.classList.remove(CLOSING_CLASS);
    this.restoreName(panel);
    panel.open = true;
    slide(wrapper, from, true);
  }

  /**
   * Slide a panel's body up, and only then actually close it - `<details>` sets
   * its contents to `display: none` on close, which would cut the animation off
   * at frame one.
   */
  closePanel(panel) {
    const wrapper = this.wrapperOf(panel);
    if (!wrapper) {
      panel.open = false;
      return;
    }

    panel.classList.add(CLOSING_CLASS);
    // Detach the exclusivity name for the length of the animation: with it in
    // place the browser slams this panel shut the moment a sibling opens, and
    // the panel is still `open` for the whole slide.
    if (panel.hasAttribute('name')) {
      panel[DETACHED_NAME] = panel.getAttribute('name');
      panel.removeAttribute('name');
    }

    slide(wrapper, wrapper.offsetHeight, false, () => {
      panel.classList.remove(CLOSING_CLASS);
      panel.open = false;
      this.restoreName(panel);
    });
  }

  restoreName(panel) {
    if (panel[DETACHED_NAME] == null) return;
    panel.setAttribute('name', panel[DETACHED_NAME]);
    panel[DETACHED_NAME] = null;
  }

  /**
   * Take over the toggle so the close can outlive the click. Enter and Space on a
   * `<summary>` dispatch a click too, so this covers the keyboard as well.
   */
  onClick(e) {
    const summary = e.target.closest && e.target.closest('summary');
    if (!summary) return;
    const panel = summary.parentElement;
    // Not one of ours means a nested accordion's; let it handle itself.
    if (!panel || !this.panels.includes(panel)) return;
    if (!this.wrapperOf(panel)) return;

    e.preventDefault();
    if (panel.open && !panel.classList.contains(CLOSING_CLASS)) this.closePanel(panel);
    else this.openPanel(panel);
  }

  onKeyDown(e) {
    const summary = e.target.closest && e.target.closest('summary');
    if (!summary) return;
    const headers = this.headers;
    const current = headers.indexOf(summary);
    // -1 means the summary belongs to a nested accordion; let it handle itself.
    if (current === -1) return;

    const next = nextIndex(current, e.key, headers.length);
    if (next === null) return;
    e.preventDefault();
    headers[next].focus();
  }

  onToggle(e) {
    const panel = e.target;
    if (!this.panels.includes(panel)) return;
    this.dispatchEvent(new CustomEvent('accordion-toggle', {
      bubbles: true,
      detail: { panel: panel, open: panel.open }
    }));
  }

  onHashChange() {
    this.openFromHash();
  }

  /**
   * Open the panel containing the element the URL fragment points at, so a link
   * to a single question lands on it opened. Instant rather than animated: a deep
   * link should arrive at the content, not at a panel still on its way open.
   */
  openFromHash() {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    if (!target || !this.contains(target)) return;

    // Walk up so a nested panel opens its ancestors too.
    let panel = target.closest('details');
    while (panel && this.contains(panel)) {
      panel.open = true;
      panel = panel.parentElement && panel.parentElement.closest('details');
    }
  }
}

define('accordion-elemental', AccordionElemental);
